import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { loginRequest, msalConfigured, msalInstance } from './msal'

export type AuthUser = { name: string; email?: string }

/** 1–2 letter initials for an avatar, from a display name (or email local-part). */
export function initialsOf(name?: string | null): string {
  const src = (name || '').trim()
  if (!src) return '?'
  const parts = src.split(/[\s.@_-]+/).filter(Boolean)
  const a = parts[0]?.[0] ?? ''
  const b = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (a + b).toUpperCase() || '?'
}

type AuthCtx = {
  user: AuthUser | null
  isAuthenticated: boolean
  /** whether a real Entra app registration is configured in this build */
  configured: boolean
  signInEntra: () => void
  signOut: () => void
}

const Ctx = createContext<AuthCtx | null>(null)

/**
 * Auth session — Azure Entra only. Sign-in is a real Entra redirect (MSAL) + silent
 * token; the app's *role* is a separate client-side switch (see app/role), not derived
 * from the token. On first sign-in the backend clones the demo templates into the
 * user's private workspace (GET /me, gated by RequireAuth).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user] = useState<AuthUser | null>(() => {
    const acc = msalInstance?.getAllAccounts()[0]
    return acc ? { name: acc.name ?? acc.username, email: acc.username } : null
  })

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      isAuthenticated: user != null,
      configured: msalConfigured,
      signInEntra: () => {
        // prompt=select_account so SSO doesn't silently reuse an existing Microsoft
        // session — the user always picks which account (e.g. work vs personal).
        msalInstance?.loginRedirect({ ...loginRequest, prompt: 'select_account' })
      },
      signOut: () => {
        if (msalInstance?.getAllAccounts().length) msalInstance.logoutRedirect()
      },
    }),
    [user],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
